import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  getProducts,
  getSKUs,
  createProduct,
  listExtensionPurchases,
  createExtensionPurchase,
  updateExtensionPurchase,
  deleteExtensionPurchase,
  openExtensionPurchaseReceipt,
  getPurchaseCategoriesSettings,
} from '../../app/api';
import { useTranslation } from '../../app/LanguageContext';
import { applyCurrencyMask, parseCurrency, formatCurrency, formatNumberAsCurrencyMask } from '../../app/currency';
import { isoYmdToDisplay, displayToIsoYmd } from '../../app/date';
import CalendarDateTextField from '../../components/CalendarDateTextField';
import { useNavigate } from 'react-router-dom';

const RECEIPT_MAX_BYTES = 10 * 1024 * 1024;

function isReceiptTypeAllowed(file) {
  if (!file) return false;
  const t = (file.type || '').toLowerCase();
  if (t === 'application/pdf' || t.startsWith('image/')) return true;
  return /\.(pdf|png|jpe?g|gif|webp|bmp)$/i.test(String(file.name || ''));
}

/** Image preview: MIME or extension (Windows clipboard often has empty type). */
function isReceiptImageFile(file) {
  if (!file) return false;
  const t = (file.type || '').toLowerCase();
  if (t.startsWith('image/')) return true;
  return /\.(png|jpe?g|gif|webp|bmp)$/i.test(String(file.name || ''));
}

function formatForDateLocal(d = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** YYYY-MM-DD from API (purchased_on) or legacy purchased_at. */
function getPurchaseCalendarYmd(purchase) {
  if (!purchase) return '';
  const on = purchase.purchased_on ?? purchase.purchasedOn;
  if (on && /^\d{4}-\d{2}-\d{2}$/.test(String(on).trim())) {
    return String(on).trim();
  }
  const at = purchase.purchased_at ?? purchase.purchasedAt;
  if (!at) return '';
  const s = String(at).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return formatForDateLocal(d);
  }
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);
  return '';
}

function sortPurchasesByPurchaseDateDesc(rows) {
  if (!Array.isArray(rows) || rows.length <= 1) return Array.isArray(rows) ? rows : [];
  return [...rows].sort((a, b) => {
    const yA = getPurchaseCalendarYmd(a);
    const yB = getPurchaseCalendarYmd(b);
    if (yA !== yB) return yB.localeCompare(yA);
    const rawA = a.purchased_at ?? a.purchasedAt ?? '';
    const rawB = b.purchased_at ?? b.purchasedAt ?? '';
    const tA = typeof rawA === 'string' && rawA ? new Date(rawA).getTime() : 0;
    const tB = typeof rawB === 'string' && rawB ? new Date(rawB).getTime() : 0;
    if (Number.isFinite(tB) && Number.isFinite(tA) && tB !== tA) return tB - tA;
    const cA = new Date(a.created_at ?? a.createdAt ?? 0).getTime();
    const cB = new Date(b.created_at ?? b.createdAt ?? 0).getTime();
    if (cB !== cA) return cB - cA;
    return String(b.id || '').localeCompare(String(a.id || ''));
  });
}

/** Default list filter: a partir de 30 dias atrás; data final em branco (sem teto na consulta). */
function getDefaultListFilterDates() {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  start.setDate(start.getDate() - 30);
  return {
    from: formatForDateLocal(start),
    to: '',
  };
}

function normalizeForSearchToken(str) {
  if (str == null) return '';
  return String(str)
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

function buildSupplySkuSearchHaystack(sku) {
  const parts = [sku.code, sku.productCode, sku.productName, sku.productDescription];
  const attrs = sku.attributes;
  if (attrs && typeof attrs === 'object' && !Array.isArray(attrs)) {
    Object.keys(attrs).forEach((k) => {
      parts.push(k, attrs[k]);
    });
  }
  return normalizeForSearchToken(parts.filter((p) => p != null && String(p).trim() !== '').join(' '));
}

function filterSupplySkuAutocompleteOptions(options, params) {
  const raw = (params.inputValue || '').trim();
  if (!raw) {
    return options;
  }
  const tokens = raw
    .split(/\s+/)
    .map((t) => normalizeForSearchToken(t.trim()))
    .filter((t) => t.length > 0);
  if (tokens.length === 0) {
    return options;
  }
  return options.filter((opt) => {
    const hay = buildSupplySkuSearchHaystack(opt);
    return tokens.every((tok) => hay.includes(tok));
  });
}

function isCreateSupplyOption(o) {
  return o != null && typeof o === 'object' && o.__createSupply === true;
}

function filterSupplySkuAutocompleteOptionsWithCreate(options, params) {
  const filtered = filterSupplySkuAutocompleteOptions(options, params);
  const raw = (params.inputValue || '').trim();
  if (!raw) {
    return filtered;
  }
  if (filtered.length === 0) {
    return [{ __createSupply: true, draftName: raw }];
  }
  return filtered;
}

function supplySkuPickerIsOptionEqual(opt, val) {
  if (isCreateSupplyOption(opt) || isCreateSupplyOption(val)) return false;
  if (val == null || val === '') return opt == null;
  if (typeof val === 'string') return typeof opt === 'object' && opt !== null && opt.id === val;
  if (typeof val === 'object' && val !== null) {
    return typeof opt === 'object' && opt !== null && opt.id === val.id;
  }
  return false;
}

function i18nCategoryLabel(translate, code) {
  if (code == null || code === '') return '';
  const key = `operational_purchases.category_${String(code).toLowerCase()}`;
  const t = translate(key);
  return t === key ? String(code) : t;
}

const defaultForm = () => ({
  description: '',
  category: '',
  quantity: '1',
  unitCost: '',
  purchasedAt: formatForDateLocal(),
  notes: '',
  productId: '',
  skuId: '',
});

function purchaseRowToForm(row) {
  const ymd = getPurchaseCalendarYmd(row);
  return {
    description: row.description || '',
    category: row.category || '',
    quantity: String(row.quantity ?? '1'),
    unitCost: formatNumberAsCurrencyMask(row.unit_cost),
    purchasedAt: ymd || formatForDateLocal(),
    notes: row.notes || '',
    productId: row.product_id || '',
    skuId: row.sku_id || '',
  };
}

function isMadeToOrder(sku) {
  return !!(sku && (sku.is_made_to_order === true || sku.is_made_to_order === 1 || sku.isMadeToOrder));
}

export default function OperationalPurchasesPage() {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const descriptionInputRef = useRef(null);
  const receiptFileInputRef = useRef(null);
  const receiptNonImageLoadTimerRef = useRef(null);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [from, setFrom] = useState(() => getDefaultListFilterDates().from);
  const [to, setTo] = useState(() => getDefaultListFilterDates().to);
  const [fromDisplay, setFromDisplay] = useState(() => isoYmdToDisplay(getDefaultListFilterDates().from));
  const [toDisplay, setToDisplay] = useState(() => '');
  const [purchasedAtDisplay, setPurchasedAtDisplay] = useState('');
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [stockOnly, setStockOnly] = useState(false);
  const [receiptOnly, setReceiptOnly] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => defaultForm());
  const [supplyProducts, setSupplyProducts] = useState([]);
  const [skusByProduct, setSkusByProduct] = useState({});
  const [supplyCatalogLoading, setSupplyCatalogLoading] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreviewLoading, setReceiptPreviewLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [creatingSupply, setCreatingSupply] = useState(false);
  const [categoryDefs, setCategoryDefs] = useState([]);
  const [categoriesLoadState, setCategoriesLoadState] = useState('loading');

  const receiptImagePreviewUrl = useMemo(() => {
    if (receiptFile && isReceiptImageFile(receiptFile)) {
      return URL.createObjectURL(receiptFile);
    }
    return null;
  }, [receiptFile]);

  useEffect(() => {
    return () => {
      if (receiptImagePreviewUrl) URL.revokeObjectURL(receiptImagePreviewUrl);
    };
  }, [receiptImagePreviewUrl]);

  useEffect(() => {
    if (!receiptPreviewLoading || !receiptImagePreviewUrl) return undefined;
    const t = window.setTimeout(() => setReceiptPreviewLoading(false), 15000);
    return () => window.clearTimeout(t);
  }, [receiptPreviewLoading, receiptImagePreviewUrl]);

  const loadCategoryDefs = useCallback(async () => {
    setCategoriesLoadState('loading');
    try {
      const res = await getPurchaseCategoriesSettings();
      const list = res?.data?.categories ?? res?.categories ?? [];
      if (Array.isArray(list) && list.length > 0) {
        setCategoryDefs(list.map((c) => ({ code: c.code ?? '', label: (c.label ?? '').trim() })));
      } else {
        setCategoryDefs([]);
      }
    } catch {
      setCategoryDefs([]);
    } finally {
      setCategoriesLoadState('ready');
    }
  }, []);

  useEffect(() => {
    loadCategoryDefs();
  }, [loadCategoryDefs]);

  useEffect(() => {
    setFromDisplay(isoYmdToDisplay(from));
  }, [from]);

  useEffect(() => {
    setToDisplay(isoYmdToDisplay(to));
  }, [to]);

  useEffect(() => {
    return () => {
      if (receiptNonImageLoadTimerRef.current) {
        window.clearTimeout(receiptNonImageLoadTimerRef.current);
      }
    };
  }, []);

  const categoriesReady = categoriesLoadState === 'ready';
  const categoriesBlocked = categoriesReady && categoryDefs.length === 0;

  const categoryOptions = useMemo(() => {
    const opts = [{ value: '', label: translate('operational_purchases.category_all') }];
    for (const d of categoryDefs) {
      if (!d.code) continue;
      opts.push({
        value: d.code,
        label: d.label || i18nCategoryLabel(translate, d.code),
      });
    }
    return opts;
  }, [categoryDefs, translate]);

  const defaultCategoryCode = useMemo(() => {
    if (categoryDefs.length > 0) return categoryDefs[0].code;
    return '';
  }, [categoryDefs]);

  const categoryLabel = (c) => {
    if (c == null || c === '') return '—';
    const d = categoryDefs.find((x) => x.code === c);
    if (d?.label) return d.label;
    return i18nCategoryLabel(translate, c);
  };

  const supplySkuPickerOptions = useMemo(
    () =>
      supplyProducts.flatMap((p) =>
        (skusByProduct[p.id] || [])
          .filter(
            (s) =>
              !isMadeToOrder(s) &&
              String(s.status || 'ACTIVE').toUpperCase() === 'ACTIVE'
          )
          .map((s) => ({
            ...s,
            productName: p.name,
            productCode: p.code || '',
            productDescription: p.description || '',
          }))
      ),
    [supplyProducts, skusByProduct]
  );

  const supplySkuById = useMemo(() => {
    const m = new Map();
    supplySkuPickerOptions.forEach((s) => {
      if (s?.id) m.set(s.id, s);
    });
    return m;
  }, [supplySkuPickerOptions]);

  const supplySkuPickerLabel = useCallback(
    (option) => {
      if (option == null || typeof option !== 'object') return '';
      const su = option.stock_unit ? ` ${option.stock_unit}` : '';
      const base = `${option.code || option.id} – ${option.productName} – ${translate('operational_purchases.stock_abbr')} ${option.stock ?? 0}${su}`;
      const attrs = option.attributes;
      if (attrs && typeof attrs === 'object' && Object.keys(attrs).length > 0) {
        const attrStr = Object.entries(attrs)
          .map(([k, v]) => `${k}: ${v}`)
          .join(' | ');
        return `${base} – ${attrStr}`;
      }
      return base;
    },
    [translate]
  );

  const supplySkuAutocompleteGetOptionLabel = useCallback(
    (o) => {
      if (o == null || typeof o !== 'object') return '';
      if (isCreateSupplyOption(o)) {
        return translate('operational_purchases.create_supply_from_search', { name: o.draftName });
      }
      return supplySkuPickerLabel(o);
    },
    [translate, supplySkuPickerLabel]
  );

  const loadPurchases = useCallback(async () => {
    setLoading(true);
    try {
      const query = {
        from: from || undefined,
        to: to || undefined,
        category: category || undefined,
        q: q.trim() || undefined,
        affects_stock: stockOnly ? true : undefined,
        has_receipt: receiptOnly ? true : undefined,
      };
      const res = await listExtensionPurchases(query);
      const list = res?.data?.purchases ?? res?.purchases ?? [];
      const arr = Array.isArray(list) ? list : [];
      setPurchases(sortPurchasesByPurchaseDateDesc(arr));
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
      setPurchases([]);
    } finally {
      setLoading(false);
    }
  }, [from, to, category, q, stockOnly, receiptOnly]);

  useEffect(() => {
    if (!categoriesReady) return;
    if (categoriesBlocked) {
      setLoading(false);
      setPurchases([]);
      return;
    }
    loadPurchases();
  }, [categoriesReady, categoriesBlocked, loadPurchases]);

  const loadSupplyCatalog = useCallback(async () => {
    setSupplyCatalogLoading(true);
    try {
      const res = await getProducts();
      const list =
        res?.data?.products ?? (Array.isArray(res?.data) ? res.data : null) ?? res?.products ?? [];
      const all = Array.isArray(list) ? list : [];
      const supplyOnly = all.filter((p) => {
        const t = String(p.type || '').toUpperCase();
        const st = String(p.status || 'ACTIVE').toUpperCase();
        return t === 'SUPPLY' && st === 'ACTIVE';
      });
      setSupplyProducts(supplyOnly);
      const pairs = await Promise.all(
        supplyOnly.map(async (p) => {
          try {
            const skuRes = await getSKUs(p.id);
            const skus = Array.isArray(skuRes.data) ? skuRes.data : [];
            return [p.id, skus];
          } catch {
            return [p.id, []];
          }
        })
      );
      const next = {};
      pairs.forEach(([id, skus]) => {
        next[id] = skus;
      });
      setSkusByProduct(next);
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
      setSupplyProducts([]);
      setSkusByProduct({});
    } finally {
      setSupplyCatalogLoading(false);
    }
  }, []);

  const openDialog = () => {
    if (!categoriesReady || categoriesBlocked || !defaultCategoryCode) return;
    setEditingId(null);
    const nextForm = { ...defaultForm(), category: defaultCategoryCode };
    setForm(nextForm);
    setPurchasedAtDisplay(isoYmdToDisplay(nextForm.purchasedAt));
    setReceiptPreviewLoading(false);
    setReceiptFile(null);
    setDialogOpen(true);
    loadSupplyCatalog();
    loadCategoryDefs();
  };

  const openEditDialog = (row) => {
    if (!categoriesReady || categoriesBlocked) return;
    setEditingId(row.id);
    const nextForm = purchaseRowToForm(row);
    setForm(nextForm);
    setPurchasedAtDisplay(isoYmdToDisplay(nextForm.purchasedAt));
    setReceiptPreviewLoading(false);
    setReceiptFile(null);
    setDialogOpen(true);
    loadSupplyCatalog();
    loadCategoryDefs();
  };

  const assignReceiptFile = useCallback(
    (file, inputEl) => {
      if (receiptNonImageLoadTimerRef.current) {
        window.clearTimeout(receiptNonImageLoadTimerRef.current);
        receiptNonImageLoadTimerRef.current = null;
      }
      if (!file) {
        setReceiptPreviewLoading(false);
        setReceiptFile(null);
        if (inputEl) inputEl.value = '';
        return;
      }
      if (file.size > RECEIPT_MAX_BYTES) {
        setSnackbar({
          open: true,
          message: translate('operational_purchases.receipt_too_large'),
          severity: 'error',
        });
        if (inputEl) inputEl.value = '';
        return;
      }
      if (!isReceiptTypeAllowed(file)) {
        setSnackbar({
          open: true,
          message: translate('operational_purchases.receipt_invalid'),
          severity: 'error',
        });
        if (inputEl) inputEl.value = '';
        return;
      }
      const isImage = isReceiptImageFile(file);
      setReceiptPreviewLoading(true);
      if (isImage) {
        requestAnimationFrame(() => {
          setReceiptFile(file);
        });
      } else {
        setReceiptFile(file);
        receiptNonImageLoadTimerRef.current = window.setTimeout(() => {
          receiptNonImageLoadTimerRef.current = null;
          setReceiptPreviewLoading(false);
        }, 200);
      }
    },
    [translate]
  );

  const clearReceipt = useCallback(() => {
    if (receiptNonImageLoadTimerRef.current) {
      window.clearTimeout(receiptNonImageLoadTimerRef.current);
      receiptNonImageLoadTimerRef.current = null;
    }
    setReceiptPreviewLoading(false);
    setReceiptFile(null);
    const el = receiptFileInputRef.current;
    if (el) el.value = '';
  }, []);

  const handleReceiptInputChange = (e) => {
    const f = e.target.files?.[0];
    assignReceiptFile(f || null, e.target);
  };

  const handlePurchaseDialogPaste = useCallback(
    (e) => {
      const cd = e.clipboardData;
      if (!cd) return;
      const { files } = cd;
      if (files && files.length > 0) {
        assignReceiptFile(files[0], null);
        e.preventDefault();
        return;
      }
      const items = cd.items;
      if (!items) return;
      for (let i = 0; i < items.length; i += 1) {
        const it = items[i];
        if (it.kind !== 'file') continue;
        const f = it.getAsFile();
        if (f && isReceiptTypeAllowed(f)) {
          assignReceiptFile(f, null);
          e.preventDefault();
          return;
        }
      }
    },
    [assignReceiptFile]
  );

  const handleReceiptDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const f = e.dataTransfer?.files?.[0];
      if (f) assignReceiptFile(f, null);
    },
    [assignReceiptFile]
  );

  const handleReceiptDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      e.dataTransfer.dropEffect = 'copy';
    } catch {
      /* ignore */
    }
  }, []);

  const buildPayload = () => {
    const desc = form.description.trim();
    if (!desc) {
      return { error: translate('operational_purchases.error_description') };
    }
    const qty = parseFloat(String(form.quantity).replace(',', '.'));
    if (!Number.isFinite(qty) || qty <= 0) {
      return { error: translate('operational_purchases.error_quantity') };
    }
    const ucRaw = form.unitCost;
    const uc = typeof ucRaw === 'string' ? parseFloat(parseCurrency(ucRaw)) : Number(ucRaw);
    if (!Number.isFinite(uc) || uc < 0) {
      return { error: translate('operational_purchases.error_unit_cost') };
    }
    const total = qty * uc;
    const skuRow = form.skuId ? supplySkuById.get(form.skuId) : null;
    const affectsStock = !!(form.skuId && String(form.skuId).trim());
    const dateStr = displayToIsoYmd(purchasedAtDisplay) || (form.purchasedAt || '').trim();
    const body = {
      description: desc,
      category: form.category,
      quantity: qty,
      unit_cost: uc,
      total_cost: total,
      notes: form.notes.trim(),
      affects_stock: affectsStock,
      product_id: affectsStock ? (skuRow?.product_id || form.productId || '') : '',
      sku_id: affectsStock ? form.skuId : '',
    };
    if (dateStr) {
      body.purchased_at = dateStr;
    }
    return { body };
  };

  const focusDescription = () => {
    requestAnimationFrame(() => {
      descriptionInputRef.current?.focus();
    });
  };

  const resetFormForNext = (prev) => ({
    ...defaultForm(),
    category: prev.category,
    purchasedAt: prev.purchasedAt,
    quantity: prev.quantity,
    productId: '',
    skuId: '',
  });

  const submit = async (mode = 'close') => {
    const built = buildPayload();
    if (built.error) {
      setSnackbar({ open: true, message: built.error, severity: 'error' });
      return;
    }
    const { body } = built;
    setSaving(true);
    try {
      if (editingId) {
        await updateExtensionPurchase(editingId, body, receiptFile || undefined);
      } else {
        await createExtensionPurchase(body, receiptFile || undefined);
      }
      setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
      await loadPurchases();
      if (mode === 'next' && !editingId) {
        setForm((f) => {
          const next = resetFormForNext(f);
          setPurchasedAtDisplay(isoYmdToDisplay(next.purchasedAt));
          return next;
        });
        setReceiptPreviewLoading(false);
        setReceiptFile(null);
        focusDescription();
      } else {
        setDialogOpen(false);
        setEditingId(null);
        setReceiptPreviewLoading(false);
        setReceiptFile(null);
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(translate('operational_purchases.confirm_delete'))) return;
    try {
      await deleteExtensionPurchase(id);
      setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
      await loadPurchases();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const viewPurchaseReceipt = async (purchaseId) => {
    try {
      await openExtensionPurchaseReceipt(purchaseId);
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const createSupplyWithName = async (rawName) => {
    const name = String(rawName || '').trim();
    if (!name) {
      setSnackbar({
        open: true,
        message: translate('operational_purchases.create_supply_name_required'),
        severity: 'error',
      });
      return;
    }
    setCreatingSupply(true);
    try {
      const res = await createProduct({
        name,
        description: '',
        status: 'ACTIVE',
        type: 'SUPPLY',
      });
      const payload = res?.data;
      const productId = payload?.id;
      if (!productId) {
        throw new Error(translate('operational_purchases.create_supply_error'));
      }
      const productRow = {
        id: productId,
        code: payload.code || productId,
        name: payload.name || name,
        description: payload.description || '',
        status: payload.status || 'ACTIVE',
        type: payload.type || 'SUPPLY',
      };
      const skuRes = await getSKUs(productId);
      const skuList = Array.isArray(skuRes.data) ? skuRes.data : [];
      setSupplyProducts((prev) => {
        if (prev.some((p) => p.id === productRow.id)) return prev;
        return [...prev, productRow].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      });
      setSkusByProduct((prev) => ({ ...prev, [productId]: skuList }));
      const def = skuList.find((s) => s.is_default) || skuList[0];
      setForm((f) => ({
        ...f,
        productId,
        skuId: def?.id || '',
      }));
      setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setCreatingSupply(false);
    }
  };

  const totalSum = purchases.reduce((s, p) => s + (Number(p.total_cost) || 0), 0);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={600}>
          {translate('operational_purchases.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openDialog}
          disabled={!categoriesReady || categoriesBlocked}
        >
          {translate('operational_purchases.new')}
        </Button>
      </Box>

      {!categoriesReady ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {categoriesBlocked ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {translate('operational_purchases.blocked_body')}
              </Typography>
              <Button variant="outlined" size="small" onClick={() => navigate('/operational-purchases/settings')}>
                {translate('operational_purchases.go_to_settings')}
              </Button>
            </Alert>
          ) : null}

      <Paper variant="outlined" sx={{ p: 2, mb: 2, opacity: categoriesBlocked ? 0.6 : 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <CalendarDateTextField
            label={translate('operational_purchases.filter_from')}
            value={fromDisplay}
            onValueChange={setFromDisplay}
            isoValue={from}
            onIsoChange={(ymd) => setFrom(ymd)}
            calendarOpenLabel={translate('operational_purchases.open_calendar')}
            onBlur={() => {
              const y = displayToIsoYmd(fromDisplay);
              if (y) setFrom(y);
              else setFromDisplay(isoYmdToDisplay(from));
            }}
            inputProps={{ 'aria-label': translate('operational_purchases.filter_from') }}
            disabled={categoriesBlocked}
            sx={{ minWidth: 132 }}
          />
          <CalendarDateTextField
            label={translate('operational_purchases.filter_to')}
            value={toDisplay}
            onValueChange={setToDisplay}
            isoValue={to}
            onIsoChange={(ymd) => setTo(ymd)}
            calendarOpenLabel={translate('operational_purchases.open_calendar')}
            onBlur={() => {
              const y = displayToIsoYmd(toDisplay);
              if (y) {
                setTo(y);
                return;
              }
              if (!String(toDisplay || '').trim()) {
                setTo('');
                setToDisplay('');
                return;
              }
              setToDisplay(isoYmdToDisplay(to));
            }}
            inputProps={{ 'aria-label': translate('operational_purchases.filter_to') }}
            disabled={categoriesBlocked}
            sx={{ minWidth: 132 }}
          />
          <TextField
            size="small"
            select
            label={translate('operational_purchases.filter_category')}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            sx={{ minWidth: 180 }}
            disabled={categoriesBlocked}
          >
            {categoryOptions.map((o) => (
              <MenuItem key={o.value || 'all'} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label={translate('operational_purchases.filter_search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ minWidth: 200 }}
            disabled={categoriesBlocked}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={stockOnly}
                onChange={(e) => setStockOnly(e.target.checked)}
                disabled={categoriesBlocked}
              />
            }
            label={translate('operational_purchases.filter_stock_only')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={receiptOnly}
                onChange={(e) => setReceiptOnly(e.target.checked)}
                disabled={categoriesBlocked}
              />
            }
            label={translate('operational_purchases.filter_has_receipt_only')}
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' } }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {translate('operational_purchases.filter_period_summary', {
            from: isoYmdToDisplay(from) || '—',
            to: to ? isoYmdToDisplay(to) || '—' : translate('operational_purchases.filter_to_open'),
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {translate('operational_purchases.total_period')}:{' '}
          <strong>{formatCurrency(totalSum)}</strong>
        </Typography>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate('operational_purchases.col_date')}</TableCell>
                <TableCell>{translate('operational_purchases.col_description')}</TableCell>
                <TableCell>{translate('operational_purchases.col_category')}</TableCell>
                <TableCell align="right">{translate('operational_purchases.col_quantity')}</TableCell>
                <TableCell align="right">{translate('operational_purchases.col_total')}</TableCell>
                <TableCell>{translate('operational_purchases.col_stock')}</TableCell>
                <TableCell>{translate('operational_purchases.col_receipt')}</TableCell>
                <TableCell align="right">{translate('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">
                      {categoriesBlocked
                        ? translate('operational_purchases.blocked_table_hint')
                        : translate('operational_purchases.empty')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                purchases.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {getPurchaseCalendarYmd(p) ? isoYmdToDisplay(getPurchaseCalendarYmd(p)) : '—'}
                    </TableCell>
                    <TableCell>{p.description}</TableCell>
                    <TableCell>{categoryLabel(p.category)}</TableCell>
                    <TableCell align="right">{p.quantity}</TableCell>
                    <TableCell align="right">{formatCurrency(p.total_cost)}</TableCell>
                    <TableCell>{p.affects_stock ? translate('common.yes') : translate('common.no')}</TableCell>
                    <TableCell>
                      {p.has_receipt ? (
                        <Button size="small" onClick={() => viewPurchaseReceipt(p.id)}>
                          {translate('operational_purchases.view_receipt')}
                        </Button>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={translate('common.edit')}>
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(p)}
                          aria-label={translate('common.edit')}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={translate('common.delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => remove(p.id)}
                          aria-label={translate('common.delete')}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

        </>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => {
          if (saving) return;
          setDialogOpen(false);
          setEditingId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingId ? translate('operational_purchases.edit_title') : translate('operational_purchases.new')}</DialogTitle>
        <DialogContent
          dividers
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pt: 2,
          }}
          onPaste={handlePurchaseDialogPaste}
        >
          <TextField
            inputRef={descriptionInputRef}
            size="small"
            label={translate('operational_purchases.field_description')}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            required
            fullWidth
            InputLabelProps={form.description.trim() ? { shrink: true } : undefined}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                size="small"
                label={translate('operational_purchases.field_category')}
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                fullWidth
              >
                {categoryOptions
                  .filter((o) => o.value)
                  .map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <CalendarDateTextField
                label={translate('operational_purchases.field_date')}
                value={purchasedAtDisplay}
                onValueChange={setPurchasedAtDisplay}
                isoValue={form.purchasedAt}
                onIsoChange={(ymd) => {
                  setForm((f) => ({ ...f, purchasedAt: ymd }));
                  setPurchasedAtDisplay(isoYmdToDisplay(ymd));
                }}
                calendarOpenLabel={translate('operational_purchases.open_calendar')}
                onBlur={() => {
                  const y = displayToIsoYmd(purchasedAtDisplay);
                  if (y) setForm((f) => ({ ...f, purchasedAt: y }));
                  else setPurchasedAtDisplay(isoYmdToDisplay(form.purchasedAt));
                }}
                fullWidth
                inputProps={{ 'aria-label': translate('operational_purchases.field_date') }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                label={translate('operational_purchases.field_quantity')}
                type="number"
                value={form.quantity}
                onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                inputProps={{ min: 0, step: 'any' }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                label={translate('operational_purchases.field_unit_cost')}
                value={form.unitCost}
                onChange={(e) => {
                  const cleaned = e.target.value.toString().replace(/\D/g, '');
                  setForm((f) => ({ ...f, unitCost: cleaned === '' ? '' : applyCurrencyMask(cleaned) }));
                }}
                fullWidth
              />
            </Grid>
          </Grid>
          <TextField
            size="small"
            label={translate('operational_purchases.field_notes')}
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            fullWidth
            multiline
            minRows={2}
          />
          <Box>
            <Autocomplete
                size="small"
                loading={supplyCatalogLoading || creatingSupply}
                disabled={creatingSupply}
                options={supplySkuPickerOptions}
                filterOptions={filterSupplySkuAutocompleteOptionsWithCreate}
                getOptionLabel={supplySkuAutocompleteGetOptionLabel}
                isOptionEqualToValue={supplySkuPickerIsOptionEqual}
                value={form.skuId ? supplySkuById.get(form.skuId) ?? null : null}
                onChange={(_, option) => {
                  if (isCreateSupplyOption(option)) {
                    createSupplyWithName(option.draftName);
                    return;
                  }
                  setForm((f) => ({
                    ...f,
                    skuId: option?.id || '',
                    productId: option?.product_id || '',
                  }));
                }}
                renderOption={(props, option) => {
                  if (isCreateSupplyOption(option)) {
                    return (
                      <li {...props}>
                        <Typography component="span" color="primary">
                          {translate('operational_purchases.create_supply_from_search', { name: option.draftName })}
                        </Typography>
                      </li>
                    );
                  }
                  return <li {...props}>{supplySkuPickerLabel(option)}</li>;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={translate('operational_purchases.field_supply_sku_search')}
                    placeholder={translate('operational_purchases.select_sku')}
                    helperText={
                      supplyCatalogLoading
                        ? translate('operational_purchases.catalog_loading')
                        : supplySkuPickerOptions.length === 0
                          ? translate('operational_purchases.no_supply_skus_hint')
                          : undefined
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {supplyCatalogLoading || creatingSupply ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
          </Box>
          <Box
            onDrop={handleReceiptDrop}
            onDragOver={handleReceiptDragOver}
            aria-busy={receiptPreviewLoading}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderStyle: 'dashed',
              borderRadius: 1,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 1.5,
              bgcolor: 'action.hover',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <CloudUploadIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                {translate('operational_purchases.receipt_drop_or_paste_hint')}
              </Typography>
            </Box>
            <Button variant="outlined" component="label" size="small" sx={{ alignSelf: 'flex-start' }}>
              {translate('operational_purchases.field_receipt')}
              <input
                ref={receiptFileInputRef}
                type="file"
                hidden
                accept="application/pdf,image/*"
                onChange={handleReceiptInputChange}
              />
            </Button>
            {receiptFile ? (
              <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
                {receiptImagePreviewUrl ? (
                  <Box
                    sx={{
                      position: 'relative',
                      maxWidth: '100%',
                      minHeight: receiptPreviewLoading ? 120 : undefined,
                    }}
                  >
                    {receiptPreviewLoading ? (
                      <Box
                        role="status"
                        aria-label={translate('common.loading')}
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(0,0,0,0.06)',
                          borderRadius: 1,
                          zIndex: 1,
                        }}
                      >
                        <CircularProgress size={32} />
                      </Box>
                    ) : null}
                    <Box
                      component="img"
                      key={`${receiptFile.name}-${receiptFile.size}-${receiptFile.lastModified ?? ''}`}
                      src={receiptImagePreviewUrl}
                      alt=""
                      decoding="async"
                      onLoad={() => setReceiptPreviewLoading(false)}
                      onError={() => setReceiptPreviewLoading(false)}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: 160,
                        borderRadius: 1,
                        display: 'block',
                        opacity: receiptPreviewLoading ? 0 : 1,
                        transition: 'opacity 0.15s ease-out',
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      position: 'relative',
                      minHeight: receiptPreviewLoading ? 72 : 48,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'action.selected',
                      borderRadius: 1,
                    }}
                  >
                    {receiptPreviewLoading ? (
                      <Box role="status" aria-label={translate('common.loading')}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {(receiptFile.type || '').includes('pdf') || /\.pdf$/i.test(receiptFile.name || '')
                          ? 'PDF'
                          : translate('operational_purchases.receipt_file_attached')}
                      </Typography>
                    )}
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: receiptImagePreviewUrl ? 1 : 0 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all', flex: '1 1 auto' }}>
                    {receiptFile.name || translate('operational_purchases.receipt_untitled')}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label={translate('operational_purchases.receipt_remove')}
                    onClick={clearReceipt}
                    edge="end"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button
            size="small"
            onClick={() => {
              if (saving) return;
              setDialogOpen(false);
              setEditingId(null);
            }}
          >
            {translate('common.cancel')}
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          {!editingId ? (
            <Button size="small" variant="outlined" onClick={() => submit('next')} disabled={saving}>
              {translate('operational_purchases.save_and_new')}
            </Button>
          ) : null}
          <Button size="small" variant="contained" onClick={() => submit('close')} disabled={saving}>
            {translate('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
